import { PdfInstruction } from '../../../api/generated';
import { useEffect, useState } from 'react';
import { SelfServiceInstructionProps } from './InstructionComponent';
import { Button, Stack, Typography } from '@mui/material';
import { Download } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';

export default function PdfInstructionComponent({
  instruction,
  onReadyStatusChange,
  onInstructionChange,
}: SelfServiceInstructionProps<PdfInstruction>) {
  const { t } = useTranslation();

  const { description, pdfUrl, pdfBase64 } = instruction.parameters;

  const [url, setUrl] = useState<string>('');

  useEffect(() => {
    if (pdfUrl) {
      setUrl(pdfUrl);
    } else if (pdfBase64) {
      const cleanBase64 = pdfBase64.replace(/^data:application\/pdf;base64,/, '');
      const blob = base64ToBlob(cleanBase64);
      const blobUrl = URL.createObjectURL(blob);
      setUrl(blobUrl);

      onInstructionChange?.({
        ...instruction,
        parameters: {
          ...instruction.parameters,
          pdfBase64: '',
        },
      });
    }
  }, [pdfUrl, pdfBase64, onInstructionChange, instruction]);

  useEffect(() => {
    return () => {
      if (url.startsWith('blob:')) {
        URL.revokeObjectURL(url);
      }
    };
  }, [url]);

  const handleDownload = async () => {
    if (!url) return;

    try {
      let downloadUrl = url;
      let isTemporary = false;

      if (!url.startsWith('blob:')) {
        const response = await fetch(url);
        const blob = await response.blob();
        downloadUrl = window.URL.createObjectURL(blob);
        isTemporary = true;
      }

      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = `delivery-note-${new Date().toISOString()}.pdf`;

      document.body.appendChild(link);
      link.click();

      document.body.removeChild(link);
      if (isTemporary) {
        window.URL.revokeObjectURL(downloadUrl);
      }
    } catch (error) {
      console.error('download failed', error);
    }
  };

  useEffect(() => {
    onReadyStatusChange(true);
  }, [onReadyStatusChange]);

  return (
    <Stack sx={{ flex: 1, gap: 2 }}>
      {description && (
        <Typography variant='h6' color='text.secondary' sx={{ mb: 2 }}>
          {description}
        </Typography>
      )}
      {url && <iframe src={url} title='PDF' style={{ display: 'flex', flex: 1 }} />}
      <Button startIcon={<Download />} onClick={handleDownload}>
        {t('download.default')}
      </Button>
    </Stack>
  );
}

function base64ToBlob(base64: string, mimeType: string = 'application/pdf'): Blob {
  const byteCharacters = atob(base64);
  const byteNumbers = new Uint8Array(byteCharacters.length);

  for (let i = 0; i < byteCharacters.length; i++) {
    byteNumbers[i] = byteCharacters.charCodeAt(i);
  }

  return new Blob([byteNumbers], { type: mimeType });
}
